/**
 * Type Safety Utilities
 * 
 * Centralized utilities for resolving common TypeScript type safety patterns
 * while maintaining runtime behavior and code quality.
 */

// =====================================================
// STRING HANDLING UTILITIES
// =====================================================

/**
 * Ensures a value is a string, converting null/undefined to empty string
 * @param value - The value to convert to string
 * @returns A guaranteed string value
 */
export function ensureString(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

/**
 * Ensures a value is a string with a fallback default
 * @param value - The value to convert to string
 * @param defaultValue - The default value if conversion fails
 * @returns A guaranteed string value
 */
export function ensureStringWithDefault(value: unknown, defaultValue: string): string {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  return String(value);
}

// =====================================================
// OPTIONAL CONVERSION UTILITIES
// =====================================================

/**
 * Converts null values to undefined for optional properties
 * @param value - The value to convert
 * @returns The value or undefined if null
 */
export function toOptional<T>(value: T | null): T | undefined {
  return value === null ? undefined : value;
}

/**
 * Converts undefined to null for database fields
 * @param value - The value to convert
 * @returns The value or null if undefined
 */
export function toNullable<T>(value: T | undefined): T | null {
  return value === undefined ? null : value;
}

// =====================================================
// SAFE ACCESS UTILITIES
// =====================================================

/**
 * Safely access nested object properties with type safety
 * @param obj - The object to access
 * @param accessor - Function to access the property
 * @param defaultValue - Default value if access fails
 * @returns The accessed value or default
 */
export function safeAccess<T, R>(
  obj: T | null | undefined,
  accessor: (obj: T) => R,
  defaultValue: R
): R {
  try {
    if (obj === null || obj === undefined) {
      return defaultValue;
    }
    return accessor(obj);
  } catch {
    return defaultValue;
  }
}

/**
 * Safely access object property by key with type safety
 * @param obj - The object to access
 * @param key - The property key
 * @param defaultValue - Default value if property doesn't exist
 * @returns The property value or default
 */
export function safeProperty<T, K extends keyof T>(
  obj: T | null | undefined,
  key: K,
  defaultValue: T[K]
): T[K] {
  if (obj === null || obj === undefined) {
    return defaultValue;
  }
  return obj[key] ?? defaultValue;
}

// =====================================================
// EVENT ADAPTERS
// =====================================================

/**
 * Converts MouseEvent to PressEvent for component compatibility
 * @param mouseEvent - The mouse event to convert
 * @returns A press event compatible object
 */
export function mouseEventToPressEvent(mouseEvent: MouseEvent): {
  type: 'press';
  target: EventTarget | null;
  currentTarget: EventTarget | null;
  preventDefault: () => void;
  stopPropagation: () => void;
} {
  return {
    type: 'press',
    target: mouseEvent.target,
    currentTarget: mouseEvent.currentTarget,
    preventDefault: () => mouseEvent.preventDefault(),
    stopPropagation: () => mouseEvent.stopPropagation(),
  };
}

/**
 * Creates a safe event handler that prevents default behavior
 * @param handler - The event handler function
 * @returns A safe event handler
 */
export function createSafeEventHandler<T extends Event>(
  handler: (event: T) => void
): (event: T) => void {
  return (event: T) => {
    try {
      event.preventDefault();
      event.stopPropagation();
      handler(event);
    } catch (error) {
      console.error('Event handler error:', error);
    }
  };
}

/**
 * Creates an input change handler that extracts value from React ChangeEvent
 * @param handler - Function that receives the input value
 * @returns A React ChangeEvent handler
 */
export function createInputChangeHandler(
  handler: (value: string) => void
): (e: React.ChangeEvent<HTMLInputElement>) => void {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    handler(e.target.value);
  };
}

// =====================================================
// TYPE GUARDS
// =====================================================

/**
 * Type guard to check if value is a non-empty string
 * @param value - The value to check
 * @returns True if value is a non-empty string
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Type guard to check if value is not null
 * @param value - The value to check
 * @returns True if value is not null
 */
export function isNotNull<T>(value: T | null): value is T {
  return value !== null;
}

/**
 * Type guard to check if value is not null or undefined
 * @param value - The value to check
 * @returns True if value is not null or undefined
 */
export function isNotNullOrUndefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Type guard to check if value is a valid object
 * @param value - The value to check
 * @returns True if value is a non-null object
 */
export function isValidObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// =====================================================
// OBJECT MANIPULATION UTILITIES
// =====================================================

/**
 * Safely omit properties from an object, handling extra properties
 * @param obj - The source object
 * @param keys - Keys to omit
 * @returns Object with specified keys omitted
 */
export function safeOmit<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }
  return result;
}

/**
 * Safely pick properties from an object
 * @param obj - The source object
 * @param keys - Keys to pick
 * @returns Object with only specified keys
 */
export function safePick<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
}

// =====================================================
// CONVERSION UTILITIES
// =====================================================

/**
 * Converts a value to a boolean with safe fallback
 * @param value - The value to convert
 * @param defaultValue - Default boolean value
 * @returns Boolean representation of the value
 */
export function safeBoolean(value: unknown, defaultValue = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1';
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return defaultValue;
}

/**
 * Converts a value to a number with safe fallback
 * @param value - The value to convert
 * @param defaultValue - Default numeric value
 * @returns Numeric representation of the value
 */
export function safeNumber(value: unknown, defaultValue = 0): number {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
}

// =====================================================
// VALIDATION UTILITIES
// =====================================================

/**
 * Validates that all required properties exist on an object
 * @param obj - The object to validate
 * @param requiredKeys - Array of required property names
 * @returns True if all required properties exist
 */
export function hasRequiredProperties<T extends Record<string, unknown>>(
  obj: T,
  requiredKeys: string[]
): boolean {
  return requiredKeys.every(key => key in obj && obj[key] !== undefined);
}

/**
 * Creates a type-safe object builder that validates required properties
 * @param requiredProperties - Object with required property names and their types
 * @returns A builder function for creating valid objects
 */
export function createTypeBuilder<T extends Record<string, unknown>>(
  requiredProperties: (keyof T)[]
) {
  return function(obj: Partial<T>): T | null {
    const hasAllRequired = requiredProperties.every(
      key => key in obj && obj[key] !== undefined
    );
    
    if (!hasAllRequired) {
      return null;
    }
    
    return obj as T;
  };
}

// =====================================================
// FORM HANDLER UTILITIES
// =====================================================

/**
 * Creates a type-safe form submission handler that preserves event handling
 * @param handler - The form submission handler
 * @returns A safe form submission wrapper
 */
export function createSafeFormHandler<T extends Record<string, unknown>>(
  handler: (data: T) => void | Promise<void>
): (data: T) => void | Promise<void> {
  return async (data: T) => {
    try {
      await handler(data);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error; // Re-throw to let form handle error display
    }
  };
}

/**
 * Adapts a form handler for use with React Hook Form handleSubmit
 * @param handler - The form handler function
 * @returns A properly typed handler for React Hook Form
 */
export function adaptFormHandler<T extends Record<string, unknown>>(
  handler: (data: T, event?: React.FormEvent) => void | Promise<void>
): (data: T, event?: React.FormEvent) => void | Promise<void> {
  return handler;
}

// =====================================================
// ENUM AND UNION TYPE UTILITIES
// =====================================================

/**
 * Creates a type-safe enum checker
 * @param enumObject - The enum object to check against
 * @param fallback - Fallback value if not valid
 * @returns Function that checks if value is valid enum value
 */
export function createEnumChecker<T extends Record<string, string | number>>(
  enumObject: T,
  fallback: T[keyof T]
) {
  return function(value: string | number | null | undefined): T[keyof T] {
    const enumValues = Object.values(enumObject);
    if (value !== null && value !== undefined && enumValues.includes(value as any)) {
      return value as T[keyof T];
    }
    return fallback;
  };
}

/**
 * Creates a color variant checker for UI components
 * @param validColors - Array of valid color strings
 * @param fallback - Fallback color if invalid
 * @returns Function that ensures valid color
 */
export function createColorChecker<T extends readonly string[]>(
  validColors: T, 
  fallback: T[number] = 'default' as T[number]
) {
  return function(color: string | null | undefined): T[number] {
    if (color && validColors.includes(color as T[number])) {
      return color as T[number];
    }
    return fallback;
  };
}

/**
 * Creates an icon name checker for icon components
 * @param validIcons - Array of valid icon names
 * @param fallback - Fallback icon name
 * @returns Function that ensures valid icon name
 */
export function createIconChecker<T extends string>(
  validIcons: readonly T[],
  fallback: T
) {
  return function(icon: string | null | undefined): T {
    if (icon && validIcons.includes(icon as T)) {
      return icon as T;
    }
    return fallback;
  };
}

// =====================================================
// INTERACTION UTILITIES
// =====================================================

/**
 * Creates a safe MyInteractionItem from partial data
 * @param data - Partial interaction data
 * @returns A valid MyInteractionItem or null if required fields are missing
 */
export function createSafeMyInteraction(
  data: Record<string, unknown> | null | undefined
): {
  id: string;
  createdAt: string;
  updatedAt: string;
  myFavourite: boolean;
  myRating: number | null;
  myRecommends: number;
  hasViewed: boolean;
  hasCommented: boolean;
  hasShared: boolean;
} | null {
  if (!data || !isValidObject(data)) {
    return null;
  }

  // Check for required fields
  if (!data.id || !data.createdAt || !data.updatedAt) {
    return null;
  }

  return {
    id: ensureString(data.id),
    createdAt: ensureString(data.createdAt),
    updatedAt: ensureString(data.updatedAt),
    myFavourite: safeBoolean(data.myFavourite),
    myRating: typeof data.myRating === 'number' ? data.myRating : null,
    myRecommends: safeNumber(data.myRecommends),
    hasViewed: safeBoolean(data.hasViewed),
    hasCommented: safeBoolean(data.hasCommented),
    hasShared: safeBoolean(data.hasShared)
  };
}

/**
 * Safely converts an array of objects to MyInteractionItem array
 * @param items - Array of partial interaction items
 * @returns Array of valid MyInteractionItem objects
 */
export function createSafeMyInteractions(
  items: unknown[] | null | undefined
): {
  id: string;
  createdAt: string;
  updatedAt: string;
  myFavourite: boolean;
  myRating: number | null;
  myRecommends: number;
  hasViewed: boolean;
  hasCommented: boolean;
  hasShared: boolean;
}[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map(item => createSafeMyInteraction(item as Record<string, unknown>))
    .filter(isNotNull);
}

// =====================================================
// PRISMA QUERY UTILITIES
// =====================================================

/**
 * Creates a safe where clause for Prisma queries handling optional filters
 * @param filters - Object with potential null/undefined values
 * @returns Sanitized where clause
 */
export function createSafeWhereClause<T extends Record<string, unknown>>(
  filters: T
): Partial<T> {
  const where: Partial<T> = {};
  
  for (const [key, value] of Object.entries(filters)) {
    if (value !== null && value !== undefined && value !== '') {
      (where as Record<string, unknown>)[key] = value;
    }
  }
  
  return where;
}

/**
 * Creates safe pagination parameters for Prisma queries
 * @param page - Page number (1-based)
 * @param limit - Items per page
 * @returns Object with skip and take values
 */
export function createSafePagination(
  page: number | string | null | undefined,
  limit: number | string | null | undefined
): { skip: number; take: number } {
  const pageNum = safeNumber(page, 1);
  const limitNum = safeNumber(limit, 10);
  
  const safePage = Math.max(1, pageNum);
  const safeLimit = Math.max(1, Math.min(100, limitNum)); // Cap at 100
  
  return {
    skip: (safePage - 1) * safeLimit,
    take: safeLimit
  };
}